/* eslint-disable */
import {
  Group,
  MirroredRepeatWrapping,
  RepeatWrapping,
  NearestMipMapLinearFilter,
  LinearMipMapLinearFilter,
  NearestFilter,
  Vector2,
  Material,
} from 'three';

import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';

import * as Helpers from './helpers';

import type { ResourceManager } from './ResourceManager';

export class Object3D {
  index: number;
  indexShift: number;

  group: Group;

  materialDefinitions: Record<string, any>;
  materials: Record<string, Material>;

  maxAnisotropy: number;

  attributes: Record<string, any>;
  parameters: Record<string, any>;

  renderFunc: () => void;
  resourceManager: ResourceManager;

  shift: number;
  symmetrical: boolean;

  constructor(...args) {
    this.group = new Group();
    this.group.castShadow = true;
    this.group.receiveShadow = true;

    this.materialDefinitions = {};
    this.materials = {};
    this.attributes = {};
    this.parameters = {};

    this.renderFunc = args[1];
    this.maxAnisotropy = args[2];
    this.resourceManager = args[3];

    this.index = 0;
    this.indexShift = Math.round(Math.random() * 100);

    this.symmetrical = false;
  }

  update(attributes) {
    this.index = this.indexShift;

    for (let i = this.group.children.length - 1; i >= 0; i--) {
      this.disposeObject(this.group.children[i]);
      this.group.remove(this.group.children[i]);
    }

    this.updateModelAttributes(attributes);
    this.updateModelParameters(attributes);
    this.calculateModelParameters();

    if (!attributes.hidden) {
      this.buildModel();
      this.renderFunc();
    }
  }

  buildModel() {
    throw new Error('Each model need a buildModel function!');
  }

  updateModelAttributes(attributes) {
    /* assign the attributes */
    this.attributes = attributes;

    /* update the standard parameters */
    this.parameters.width = +(attributes.width || this.parameters.width);
    this.parameters.height = +(attributes.height || this.parameters.height);
    this.parameters.track = +(attributes.track || this.parameters.track);
    this.parameters.double =
      attributes.configuration === 'configuration-double' ? true : false;
    this.parameters.orientation =
      attributes.orientation === 'orientation-right' ? 'right' : 'left';
    this.symmetrical = this.parameters.orientation === 'right';
  }

  updateModelParameters(attributes) {
    throw new Error('Each model need a updateModelParamters function!');
  }

  calculateModelParameters() {
    throw new Error('Each model need a calculateModelParameters function!');
  }

  initMaterials() {
    Object.keys(this.materials).forEach((key) => {
      this.setMaterial(this.materials[key], this.materialDefinitions[key]);
    });
  }

  setMaterial(material, mtlDefinition) {
    if (material && mtlDefinition) {
      // Name
      if (mtlDefinition.name) {
        material.name = mtlDefinition.name;
      }

      // Side
      if (mtlDefinition.side) {
        material.side = mtlDefinition.side;
      }

      // Color
      if (mtlDefinition.color) {
        material.color.set(mtlDefinition.color);
        // material.color.setHex(mtlDefinition.color).convertSRGBToLinear();
      } else {
        material.color.set(null);
      }

      // Albedo
      if (mtlDefinition.map) {
        this.resourceManager.getTexture(mtlDefinition.map, (texture) => {
          if (mtlDefinition.uvScale) {
            texture.repeat.set(
              mtlDefinition.uvScale.u,
              mtlDefinition.uvScale.v
            );
          }
          if (mtlDefinition.wrap === MirroredRepeatWrapping) {
            texture.wrapS = MirroredRepeatWrapping;
            texture.wrapT = MirroredRepeatWrapping;
          } else {
            texture.wrapS = RepeatWrapping;
            texture.wrapT = RepeatWrapping;
          }
          texture.anisotropy = this.maxAnisotropy;
          texture.minFilter = LinearMipMapLinearFilter;
          texture.magFilter = NearestFilter;
          if (mtlDefinition.colorSpace) {
            texture.colorSpace = mtlDefinition.colorSpace;
          }
          texture.needsUpdate = true;

          material.map = texture;
        });
      } else {
        material.map = null;
      }

      // Normal
      if (mtlDefinition.normalMap) {
        this.resourceManager.getTexture(mtlDefinition.normalMap, (texture) => {
          if (mtlDefinition.uvScale) {
            texture.repeat.set(
              mtlDefinition.uvScale.u,
              mtlDefinition.uvScale.v
            );
          }
          //if (mtlDefinition.wrap === MirroredRepeatWrapping) {
          //  texture.wrapS = MirroredRepeatWrapping;
          //  texture.wrapT = MirroredRepeatWrapping;
          //} else {
          texture.wrapS = RepeatWrapping;
          texture.wrapT = RepeatWrapping;
          //}
          texture.anisotropy = this.maxAnisotropy;
          texture.minFilter = LinearMipMapLinearFilter;
          texture.magFilter = NearestFilter;
          texture.needsUpdate = true;

          material.normalMap = texture;

          if (mtlDefinition.normalScale) {
            material.normalScale = new Vector2(
              mtlDefinition.normalScale.x,
              mtlDefinition.normalScale.y
            );
          }
        });
      } else {
        material.normalMap = null;
      }

      // Bump
      if (mtlDefinition.bumpMap) {
        this.resourceManager.getTexture(mtlDefinition.bumpMap, (texture) => {
          if (mtlDefinition.uvScale) {
            texture.repeat.set(
              mtlDefinition.uvScale.u,
              mtlDefinition.uvScale.v
            );
          }
          if (mtlDefinition.wrap === MirroredRepeatWrapping) {
            texture.wrapS = MirroredRepeatWrapping;
            texture.wrapT = MirroredRepeatWrapping;
          } else {
            texture.wrapS = RepeatWrapping;
            texture.wrapT = RepeatWrapping;
          }
          texture.anisotropy = this.maxAnisotropy;
          texture.minFilter = LinearMipMapLinearFilter;
          texture.magFilter = NearestFilter;
          texture.needsUpdate = true;

          material.bumpMap = texture;
        });
      } else {
        material.bumpMap = null;
      }
      if (mtlDefinition.bumpScale) {
        material.bumpScale = mtlDefinition.bumpScale;
      }

      // Displacement
      if (mtlDefinition.displacementMap) {
        this.resourceManager.getTexture(
          mtlDefinition.displacementMap,
          (texture) => {
            if (mtlDefinition.uvScale) {
              texture.repeat.set(
                mtlDefinition.uvScale.u,
                mtlDefinition.uvScale.v
              );
            }
            if (mtlDefinition.wrap === MirroredRepeatWrapping) {
              texture.wrapS = MirroredRepeatWrapping;
              texture.wrapT = MirroredRepeatWrapping;
            } else {
              texture.wrapS = RepeatWrapping;
              texture.wrapT = RepeatWrapping;
            }
            texture.anisotropy = this.maxAnisotropy;
            texture.minFilter = LinearMipMapLinearFilter;
            texture.magFilter = NearestFilter;
            texture.needsUpdate = true;

            material.displacementMap = texture;

            if (mtlDefinition.displacementScale) {
              material.displacementScale = mtlDefinition.displacementScale;
            }
            if (mtlDefinition.displacementBias) {
              material.displacementBias = mtlDefinition.displacementBias;
            }
          }
        );
      } else {
        material.displacementMap = null;
      }

      // Roughness
      if (mtlDefinition.roughnessMap) {
        this.resourceManager.getTexture(
          mtlDefinition.roughnessMap,
          (texture) => {
            if (mtlDefinition.uvScale) {
              texture.repeat.set(
                mtlDefinition.uvScale.u,
                mtlDefinition.uvScale.v
              );
            }
            if (mtlDefinition.wrap === MirroredRepeatWrapping) {
              texture.wrapS = MirroredRepeatWrapping;
              texture.wrapT = MirroredRepeatWrapping;
            } else {
              texture.wrapS = RepeatWrapping;
              texture.wrapT = RepeatWrapping;
            }
            texture.anisotropy = this.maxAnisotropy;
            texture.minFilter = LinearMipMapLinearFilter;
            texture.magFilter = NearestFilter;
            texture.needsUpdate = true;

            material.roughnessMap = texture;
          }
        );
      } else {
        material.roughnessMap = null;
      }
      if (mtlDefinition.roughness) {
        material.roughness = mtlDefinition.roughness;
      }

      // Metalness
      if (mtlDefinition.metalnessMap === '') {
        this.resourceManager.getTexture(
          mtlDefinition.metalnessMap,
          (texture) => {
            if (mtlDefinition.uvScale) {
              texture.repeat.set(
                mtlDefinition.uvScale.u,
                mtlDefinition.uvScale.v
              );
            }
            if (mtlDefinition.wrap === MirroredRepeatWrapping) {
              texture.wrapS = MirroredRepeatWrapping;
              texture.wrapT = MirroredRepeatWrapping;
            } else {
              texture.wrapS = RepeatWrapping;
              texture.wrapT = RepeatWrapping;
            }
            texture.anisotropy = this.maxAnisotropy;
            texture.minFilter = LinearMipMapLinearFilter;
            texture.magFilter = NearestFilter;
            texture.needsUpdate = true;

            material.metalnessMap = texture;
          }
        );
      } else {
        material.metalnessMap = null;
      }

      if (mtlDefinition.metalness) {
        material.metalness = mtlDefinition.metalness;
      }

      // Alpha
      if (mtlDefinition.alphaMap) {
        this.resourceManager.getTexture(mtlDefinition.alphaMap, (texture) => {
          if (mtlDefinition.uvScale) {
            texture.repeat.set(
              mtlDefinition.uvScale.u,
              mtlDefinition.uvScale.v
            );
          }
          if (mtlDefinition.wrap === MirroredRepeatWrapping) {
            texture.wrapS = MirroredRepeatWrapping;
            texture.wrapT = MirroredRepeatWrapping;
          } else {
            texture.wrapS = RepeatWrapping;
            texture.wrapT = RepeatWrapping;
          }
          texture.anisotropy = this.maxAnisotropy;
          texture.minFilter = LinearMipMapLinearFilter;
          texture.magFilter = NearestFilter;
          texture.needsUpdate = true;

          material.alphaMap = texture;
        });
      } else {
        material.alphaMap = null;
      }
      if (mtlDefinition.opacity) {
        material.opacity = mtlDefinition.opacity;
      }

      // AO
      if (mtlDefinition.aoMap) {
        this.resourceManager.getTexture(mtlDefinition.aoMap, (texture) => {
          if (mtlDefinition.uvScale) {
            texture.repeat.set(
              mtlDefinition.uvScale.u,
              mtlDefinition.uvScale.v
            );
          }
          //if (mtlDefinition.wrap === MirroredRepeatWrapping) {
          //  texture.wrapS = MirroredRepeatWrapping;
          //  texture.wrapT = MirroredRepeatWrapping;
          //} else {
          texture.wrapS = RepeatWrapping;
          texture.wrapT = RepeatWrapping;
          //}
          texture.anisotropy = this.maxAnisotropy;
          texture.minFilter = LinearMipMapLinearFilter;
          texture.magFilter = NearestFilter;
          texture.needsUpdate = true;

          material.aoMap = texture;
        });
      } else {
        material.aoMap = null;
      }
      if (mtlDefinition.aoMapIntensity) {
        material.aoMapIntensity = mtlDefinition.aoMapIntensity;
      }

      // Emissive
      if (mtlDefinition.emissiveMap) {
        this.resourceManager.getTexture(
          mtlDefinition.emissiveMap,
          (texture) => {
            if (mtlDefinition.uvScale) {
              texture.repeat.set(
                mtlDefinition.uvScale.u,
                mtlDefinition.uvScale.v
              );
            }
            if (mtlDefinition.wrap === MirroredRepeatWrapping) {
              texture.wrapS = MirroredRepeatWrapping;
              texture.wrapT = MirroredRepeatWrapping;
            } else {
              texture.wrapS = RepeatWrapping;
              texture.wrapT = RepeatWrapping;
            }
            texture.anisotropy = this.maxAnisotropy;
            texture.minFilter = LinearMipMapLinearFilter;
            texture.magFilter = NearestFilter;
            if (mtlDefinition.colorSpace) {
              texture.colorSpace = mtlDefinition.colorSpace;
            }
            texture.needsUpdate = true;

            material.emissiveMap = texture;

            if (mtlDefinition.emissiveIntensity) {
              material.emissiveIntensity = mtlDefinition.emissiveIntensity;
            }
          }
        );
        material.emissiveMap = null;
      } else {
        material.emissiveMap = null;
      }
      if (mtlDefinition.emissive) {
        material.emissive.set(mtlDefinition.emissive);
        // material.emissive.setHex(mtlDefinition.emissive).convertSRGBToLinear();
      }

      // Env
      if (mtlDefinition.envMapIntensity) {
        material.envMapIntensity = mtlDefinition.envMapIntensity;
      }
      if (mtlDefinition.wireframe) {
        material.wireframe = mtlDefinition.wireframe;
      }
      if (mtlDefinition.transparent) {
        material.transparent = mtlDefinition.transparent;
      }

      // PBR
      if (mtlDefinition.transmission) {
        material.transmission = mtlDefinition.transmission;
      }
      if (mtlDefinition.thickness) {
        material.thickness = mtlDefinition.thickness;
      }
      if (mtlDefinition.clearcoat) {
        material.clearcoat = mtlDefinition.clearcoat;
      }
      if (mtlDefinition.clearcoatRoughness) {
        material.clearcoatRoughness = mtlDefinition.clearcoatRoughness;
      }

      // MeshPhongMaterial
      if (mtlDefinition.reflectivity) {
        material.reflectivity = mtlDefinition.reflectivity;
      }

      material.needsUpdate = true;
      this.renderFunc();
    }
  }

  updateMaterial(material, mtlDefinition) {
    if (material && mtlDefinition) {
      // Color
      if (mtlDefinition.color) {
        material.color.set(mtlDefinition.color);
        // material.color.setHex(mtlDefinition.color).convertSRGBToLinear();
      } else {
        material.color.set(null);
      }

      // Albedo
      if (mtlDefinition.map) {
        this.resourceManager.getTexture(mtlDefinition.map, (texture) => {
          if (mtlDefinition.uvScale) {
            texture.repeat.set(
              mtlDefinition.uvScale.u,
              mtlDefinition.uvScale.v
            );
          }
          if (mtlDefinition.wrap === MirroredRepeatWrapping) {
            texture.wrapS = MirroredRepeatWrapping;
            texture.wrapT = MirroredRepeatWrapping;
          } else {
            texture.wrapS = RepeatWrapping;
            texture.wrapT = RepeatWrapping;
          }
          texture.anisotropy = this.maxAnisotropy;
          texture.minFilter = NearestMipMapLinearFilter;
          texture.magFilter = NearestFilter;
          texture.needsUpdate = true;
          material.map = texture;
          if (mtlDefinition.colorSpace) {
            material.map.colorSpace = mtlDefinition.colorSpace;
          }
        });
      } else {
        material.map = null;
      }

      // Emissive
      if (mtlDefinition.emissive) {
        material.emissive.set(mtlDefinition.emissive);
        // material.emissive.setHex(mtlDefinition.emissive).convertSRGBToLinear();
      }
      material.needsUpdate = true;

      this.renderFunc();
    }
  }

  applyMaterial(obj, material, dispose = true) {
    if (!this.isRenderItem(obj)) {
      return;
    }
    if (dispose && obj.material) {
      this.disposeMaterial(obj);
    }
    obj.material = material;
  }

  updatePosition(cameraPosition, open = true) {
    /* determine the position of the camera ie front or back of the door */
    const pos = cameraPosition.clone();
    const zoom = Math.abs(pos.y) < 2 ? 1 : 0;

    pos.normalize;
    const shift = 0;
    const slide = shift ? shift : open ? zoom : 1;
    // const flip = shift ? shift : zoom;

    const overlap = 50;
    /* set the default position of the object */
    this.group.position.z = -this.parameters.height / 2 - 100;
    if (this.parameters.double) {
      this.group.position.x =
        this.parameters.orientation === 'right'
          ? -(this.parameters.width - overlap) * (1 - slide) +
            -this.parameters.width
          : (this.parameters.width - overlap) * (1 - slide);
    } else {
      this.group.position.x =
        this.parameters.orientation === 'right'
          ? -this.parameters.width * (1 - slide) - overlap * slide
          : -(this.parameters.width - overlap) * slide;
    }
  }

  updateGeometry(geometry, angle = 0, size = 1024, vScale = 1) {
    this.index++;
    if (this.symmetrical) {
      geometry = Helpers.MirrorObjectX(geometry, false);
      geometry.translate(this.parameters.width, 0, 0);
    }
    Helpers.updateUVs(
      geometry,
      this.symmetrical ? -angle : angle,
      this.index,
      size,
      vScale
    );
    return geometry;
  }

  isRenderItem(obj) {
    return 'geometry' in obj && 'material' in obj;
  }

  disposeMaterial(obj) {
    if (!this.isRenderItem(obj)) {
      return;
    }
    const materials = [].concat(obj.material);

    for (const material of materials) {
      material.dispose();
    }
  }

  disposeObject(obj) {
    // Handle CSS2DObject specifically
    if (obj instanceof CSS2DObject) {
      if (obj.element && obj.element.parentNode) {
        obj.element.parentNode.removeChild(obj.element);
      }
      obj = null;
      return;
    }

    // Recursively dispose children
    while (obj.children.length > 0) {
      this.disposeObject(obj.children[0]);
      obj.remove(obj.children[0]);
    }

    // Dispose geometry and material
    if (obj.geometry) {
      obj.geometry.dispose();
    }
    if (obj.material) {
      this.disposeMaterial(obj.material);
    }

    // Remove from parent
    //if (obj.parent) {
    //    obj.parent.remove(obj);
    //}

    // Clear references
    //obj = null;
  }

  // Usage Example
  // disposeObject(yourObject3D);
  // yourObject3D = null; // Remove reference
}
